/* pg.js — Solaya Bloom interactions for the Parkwood theme (xqskdw-v0).
   GSAP (+ScrollTrigger) is loaded as classic deferred scripts before this file; three.js is imported lazily only when the
   hero scrolls into view, WebGL works and the visitor has not asked for reduced motion.
   Everything degrades: no GSAP -> content simply shows; no WebGL -> the product photo in the fallback card stays.
   Scroll rule (Ninos 2026-10-26): reveals are CSS transitions flipped by IntersectionObserver, nothing is scrubbed by scroll,
   no backdrop blur, the WebGL loop pauses off-screen. */
(function () {
  'use strict';
  var doc = document, root = doc.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var g = window.gsap, ST = window.ScrollTrigger;
  var hasGsap = !!(g && ST) && !reduced;
  if (hasGsap) { g.registerPlugin(ST); }

  /* ---------------- Reveals (v2: CSS transitions + IO, only items below the fold at boot) ---------------- */
  function markReveals(scope) {
    var sel = [
      '.pp-section .pp-eyebrow', '.pp-section .pp-h2', '.pp-section .pp-sub', '.pp-section .pp-text', '.pp-section .pp-cta',
      '.vc-card', '.pp-gb', '.pp-faq__item', '.pp-guarantee__card', '.pp-media', '.vc-results__row', '.vc-results__kicker',
      '.vc-results__text', '.vc-results__caption', '.pp-pct', '.testimonial-card', '.collection .card-wrapper',
      '.collection-title', '.comparison-table-container', '.content-and-comparison-table .content-container',
      '.featured-product .product__info-container > *', '.featured-product .product__media-wrapper',
      '.pg-anatomy__frame', '.pg-step', '.pg-anatomy__cta', '.newsletter__wrapper > *'
    ].join(',');
    var nodes = (scope || doc).querySelectorAll(sel);
    nodes.forEach(function (n) {
      if (n.closest('.pg-hero')) return;
      if (!n.hasAttribute('data-pg-reveal')) n.setAttribute('data-pg-reveal', '');
    });
    return nodes;
  }
  function runReveals() {
    if (reduced || !('IntersectionObserver' in window)) return;
    var vh = window.innerHeight || 800, list = [];
    markReveals().forEach(function (n) {
      if (n.classList.contains('pg-in') || n.classList.contains('pg-done')) return;
      if (n.getBoundingClientRect().top > vh) list.push(n); else n.removeAttribute('data-pg-reveal');
    });
    if (!list.length) return;
    root.classList.add('pg-reveal');
    var io = new IntersectionObserver(function (entries) {
      var k = 0;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target; io.unobserve(n);
        n.style.transitionDelay = Math.min(k++, 8) * 60 + 'ms';
        n.addEventListener('transitionend', function done() { n.classList.add('pg-done'); n.style.transitionDelay = ''; n.removeEventListener('transitionend', done); });
        n.classList.add('pg-in');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    list.forEach(function (n) { io.observe(n); });
    setTimeout(function () { list.forEach(function (n) { if (!n.classList.contains('pg-in')) { n.classList.add('pg-in', 'pg-done'); } }); }, 8000);
  }

  /* ---------------- Percent rings count up ---------------- */
  function runRings() {
    if (!hasGsap) return;
    doc.querySelectorAll('.pp-pct__ring').forEach(function (ring) {
      if (ring.hasAttribute('data-pg-ring')) return; ring.setAttribute('data-pg-ring', '');
      var span = ring.querySelector('span'); if (!span) return;
      var target = parseFloat(ring.style.getPropertyValue('--pp-value')) || parseFloat(span.textContent) || 0;
      var obj = { v: 0 };
      ring.style.setProperty('--pp-value', '0'); span.textContent = '0%';
      ST.create({
        trigger: ring, start: 'top 90%', once: true,
        onEnter: function () {
          g.to(obj, { v: target, duration: 1.6, ease: 'power2.out', onUpdate: function () {
            ring.style.setProperty('--pp-value', obj.v.toFixed(1)); span.textContent = Math.round(obj.v) + '%';
          } });
        }
      });
    });
  }

  /* ---------------- Tilt + magnetic (fine pointers only) ---------------- */
  function runTilt() {
    if (!hasGsap || !finePointer) return;
    doc.querySelectorAll('.collection .card-wrapper .card, .vc-card, .pp-gb, .pg-hero__metric').forEach(function (el) {
      if (el.classList.contains('pg-tilt')) return;
      el.classList.add('pg-tilt');
      var rx = g.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
      var ry = g.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
      g.set(el, { transformPerspective: 900 });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        rx(-py * 7); ry(px * 9);
      });
      el.addEventListener('pointerleave', function () { rx(0); ry(0); });
    });
    doc.querySelectorAll('.button--primary, .pg-hero__actions .button').forEach(function (b) {
      if (b.hasAttribute('data-pg-mag')) return; b.setAttribute('data-pg-mag', '');
      var x = g.quickTo(b, 'x', { duration: 0.4, ease: 'power3' }), y = g.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        x(((e.clientX - r.left) / r.width - 0.5) * 10); y(((e.clientY - r.top) / r.height - 0.5) * 8);
      });
      b.addEventListener('pointerleave', function () { x(0); y(0); });
    });
  }

  /* ---------------- Anatomy: hotspots, hover/tap only ---------------- */
  function runAnatomy() {
    doc.querySelectorAll('[data-pg-anatomy]').forEach(function (sec) {
      if (sec.hasAttribute('data-pg-ready')) return; sec.setAttribute('data-pg-ready', '');
      var steps = [].slice.call(sec.querySelectorAll('[data-pg-step]'));
      var spots = [].slice.call(sec.querySelectorAll('[data-pg-spot]'));
      if (!steps.length) return;
      var current = -1;
      function setActive(i) {
        i = Math.max(0, Math.min(steps.length - 1, i));
        if (i === current) return;
        current = i;
        steps.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
        spots.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      }
      setActive(0);
      steps.forEach(function (s, i) { s.addEventListener('mouseenter', function () { if (finePointer) setActive(i); }); s.addEventListener('click', function () { setActive(i); }); });
      spots.forEach(function (s, i) { s.addEventListener('mouseenter', function () { if (finePointer) setActive(i); }); s.addEventListener('click', function () { setActive(i); }); });
    });
  }

  /* ---------------- Hero copy ---------------- */
  function runHeroCopy(hero) {
    var h = hero.querySelector('.pg-hero__h');
    if (h && !h.querySelector('.pg-w')) {
      h.innerHTML = h.innerHTML.replace(/(<em>.*?<\/em>|[^\s<]+)(\s*)/g, function (_, w, sp) { return '<span class="pg-w">' + w + (sp ? ' ' : '') + '</span>'; });
    }
    if (!hasGsap) return;
    var tl = g.timeline({ defaults: { ease: 'power3.out' } });
    tl.from(hero.querySelectorAll('.pp-eyebrow'), { y: 16, opacity: 0, duration: 0.7 }, 0.05)
      .from(hero.querySelectorAll('.pg-w'), { yPercent: 70, opacity: 0, duration: 0.9, stagger: 0.06 }, 0.1)
      .from(hero.querySelectorAll('.pg-hero__lead'), { y: 18, opacity: 0, duration: 0.8 }, 0.5)
      .from(hero.querySelectorAll('.pg-hero__actions .button'), { y: 14, opacity: 0, duration: 0.6, stagger: 0.1 }, 0.65)
      .from(hero.querySelectorAll('.pg-hero__metric'), { y: 18, opacity: 0, duration: 0.7, stagger: 0.08 }, 0.8)
      .from(hero.querySelectorAll('.pg-hero__chip, .pg-hero__tag'), { scale: 0.6, opacity: 0, duration: 0.6, ease: 'back.out(2)' }, 0.9)
      .from(hero.querySelectorAll('.pg-hero__row'), { y: 14, opacity: 0, duration: 0.6, stagger: 0.08 }, 1.0);
    setTimeout(function () {
      hero.querySelectorAll('.pp-eyebrow, .pg-w, .pg-hero__lead, .pg-hero__actions .button, .pg-hero__metric, .pg-hero__chip, .pg-hero__tag, .pg-hero__row').forEach(function (n) {
        if (parseFloat(getComputedStyle(n).opacity) < 1) { g.killTweensOf(n); g.set(n, { clearProps: 'all' }); }
      });
    }, 2800);
  }

  /* ---------------- Build state (shared by the 3D scene, the chip and the Build link) ---------------- */
  function heroState(hero) {
    var sw = hero.querySelector('.pg-swatch.is-on');
    var st = {
      color: sw ? sw.getAttribute('data-pg-color') : '', hex: sw ? sw.getAttribute('data-hex') : '#e6d2ab',
      wood: sw ? sw.getAttribute('data-wood') : '#e4cfa6', dark: sw ? sw.getAttribute('data-dark') === '1' : false,
      size: 'Small', name: '', defaultName: hero.getAttribute('data-default-name') || "Leo's Garage",
      onChange: []
    };
    var chip = hero.querySelector('[data-pg-chip]'), link = hero.querySelector('[data-pg-build]'), nameOut = hero.querySelector('[data-pg-color-name]');
    var base = hero.getAttribute('data-product-url') || '#';
    function emit() {
      if (chip) chip.textContent = hero.getAttribute(st.size === 'Large' ? 'data-chip-large' : 'data-chip-small') || chip.textContent;
      if (nameOut) nameOut.textContent = st.color;
      if (link && base !== '#') {
        var q = [];
        if (st.color) q.push('color=' + encodeURIComponent(st.color));
        q.push('size=' + encodeURIComponent(st.size));
        if (st.name) q.push('name=' + encodeURIComponent(st.name));
        link.setAttribute('href', base + (base.indexOf('?') > -1 ? '&' : '?') + q.join('&'));
      }
      st.onChange.forEach(function (f) { f(st); });
    }
    hero.querySelectorAll('.pg-swatch').forEach(function (b) {
      b.addEventListener('click', function () {
        hero.querySelectorAll('.pg-swatch').forEach(function (o) { o.classList.remove('is-on'); o.setAttribute('aria-checked', 'false'); });
        b.classList.add('is-on'); b.setAttribute('aria-checked', 'true');
        st.color = b.getAttribute('data-pg-color'); st.hex = b.getAttribute('data-hex'); st.wood = b.getAttribute('data-wood'); st.dark = b.getAttribute('data-dark') === '1';
        emit();
      });
    });
    hero.querySelectorAll('.pg-size').forEach(function (b) {
      b.addEventListener('click', function () {
        hero.querySelectorAll('.pg-size').forEach(function (o) { o.classList.remove('is-on'); o.setAttribute('aria-checked', 'false'); });
        b.classList.add('is-on'); b.setAttribute('aria-checked', 'true');
        st.size = b.getAttribute('data-pg-size'); emit();
      });
    });
    var input = hero.querySelector('[data-pg-name]'), t;
    if (input) input.addEventListener('input', function () { clearTimeout(t); t = setTimeout(function () { st.name = input.value.trim().slice(0, 14); emit(); }, 120); });
    emit();
    return st;
  }

  /* ---------------- Hero: three.js garage ---------------- */
  function webglOk() {
    try { var c = doc.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
  }
  function initHero3D(hero, st) {
    var canvas = hero.querySelector('.pg-hero__canvas');
    if (!canvas || reduced || !webglOk()) return;
    var src = hero.getAttribute('data-three') || 'https://cdnjs.cloudflare.com/ajax/libs/three.js/0.160.0/three.module.min.js';
    loadThree(src).then(function (THREE) { buildHero(THREE, hero, canvas, st); }).catch(function (err) { if (window.console) console.warn('pg hero 3d skipped', err); });
  }
  // Shopify minifies theme JS and rewrites a dynamic import() into require(), so the ES module is pulled in by an
  // inline <script type="module"> instead and handed over on window.
  function loadThree(src) {
    return new Promise(function (resolve, reject) {
      if (window.__pgThree) return resolve(window.__pgThree);
      window.addEventListener('pg:three', function () { resolve(window.__pgThree); }, { once: true });
      var s = doc.createElement('script'); s.type = 'module';
      s.textContent = "import * as T from '" + src + "'; window.__pgThree = T; window.dispatchEvent(new Event('pg:three'));";
      doc.head.appendChild(s);
      setTimeout(function () { if (!window.__pgThree) reject(new Error('three.js did not load')); }, 15000);
    });
  }

  function woodTexture(THREE, hex) {
    // birch plywood face: pale base, faint long grain lines, a hint of figure
    var c = doc.createElement('canvas'); c.width = 512; c.height = 512; var x = c.getContext('2d');
    x.fillStyle = hex; x.fillRect(0, 0, 512, 512);
    var r = 1;
    function rnd() { r = (r * 16807) % 2147483647; return r / 2147483647; }
    for (var i = 0; i < 110; i++) {
      var y = rnd() * 512, a = 0.04 + rnd() * 0.07, w = 1 + rnd() * 2.5;
      x.strokeStyle = 'rgba(120, 80, 40,' + a + ')'; x.lineWidth = w; x.beginPath();
      x.moveTo(-10, y); for (var k = 0; k <= 8; k++) x.lineTo(k * 64, y + Math.sin(k * 0.9 + i) * (2 + rnd() * 5)); x.stroke();
    }
    var grd = x.createLinearGradient(0, 0, 512, 0); grd.addColorStop(0, 'rgba(255,255,255,0.06)'); grd.addColorStop(0.5, 'rgba(0,0,0,0.03)'); grd.addColorStop(1, 'rgba(255,255,255,0.05)');
    x.fillStyle = grd; x.fillRect(0, 0, 512, 512);
    var t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
    return t;
  }
  function signCanvas() { var c = doc.createElement('canvas'); c.width = 1024; c.height = 256; return c; }
  function drawSign(c, text, dark, hex) {
    var x = c.getContext('2d'); x.clearRect(0, 0, c.width, c.height);
    x.fillStyle = hex; x.fillRect(0, 0, c.width, c.height);
    x.fillStyle = dark ? '#fff4e6' : '#2c1c12';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    var size = 150; x.font = 'italic 600 ' + size + 'px Fraunces, Georgia, serif';
    while (x.measureText(text).width > 920 && size > 60) { size -= 8; x.font = 'italic 600 ' + size + 'px Fraunces, Georgia, serif'; }
    x.fillText(text, 512, 136);
  }
  function shadowSprite() {
    var sc = doc.createElement('canvas'); sc.width = sc.height = 256; var sx = sc.getContext('2d');
    var grd = sx.createRadialGradient(128, 128, 10, 128, 128, 128);
    grd.addColorStop(0, 'rgba(94,58,32,0.28)'); grd.addColorStop(0.6, 'rgba(94,58,32,0.10)'); grd.addColorStop(1, 'rgba(94,58,32,0)');
    sx.fillStyle = grd; sx.fillRect(0, 0, 256, 256);
    return sc;
  }
  // side panel: a tall plate with a rounded top-front corner (the "D" profile seen in the photos). Lies in the y/z plane.
  function sidePanelGeometry(THREE, D, H, T) {
    var s = new THREE.Shape(); var r = Math.min(D * 0.55, H * 0.42);
    s.moveTo(-D / 2, 0); s.lineTo(D / 2, 0); s.lineTo(D / 2, H - r);
    s.quadraticCurveTo(D / 2, H, D / 2 - r, H);
    s.lineTo(-D / 2, H); s.closePath();
    var geo = new THREE.ExtrudeGeometry(s, { depth: T, bevelEnabled: false });
    geo.rotateY(-Math.PI / 2); geo.translate(T / 2, 0, 0);   // shape x -> +z, so the rounded corner sits at the FRONT top; centred on x
    return geo;
  }
  // ground-floor front wall with arched bays
  function archWallGeometry(THREE, W, H, T, bays) {
    var s = new THREE.Shape();
    s.moveTo(-W / 2, 0); s.lineTo(W / 2, 0); s.lineTo(W / 2, H); s.lineTo(-W / 2, H); s.closePath();
    var bw = W / bays, aw = bw * 0.68, ah = H * 0.78, rr = aw / 2;
    for (var i = 0; i < bays; i++) {
      var cx = -W / 2 + bw * (i + 0.5), hole = new THREE.Path();
      hole.moveTo(cx - aw / 2, 0); hole.lineTo(cx - aw / 2, ah - rr); hole.absarc(cx, ah - rr, rr, Math.PI, 0, true); hole.lineTo(cx + aw / 2, 0); hole.closePath();
      s.holes.push(hole);
    }
    var geo = new THREE.ExtrudeGeometry(s, { depth: T, bevelEnabled: false }); geo.translate(0, 0, -T / 2);
    return geo;
  }
  function makeCar(THREE, color, dark) {
    var car = new THREE.Group();
    var body = new THREE.MeshStandardMaterial({ color: color, roughness: 0.55, metalness: 0.05 });
    var b = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.26, 0.42), body); b.position.y = 0.26; car.add(b);
    var cab = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.22, 0.38), body); cab.position.set(-0.04, 0.5, 0); car.add(cab);
    var win = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.12, 0.4), new THREE.MeshStandardMaterial({ color: 0xfff4e6, roughness: 0.3 })); win.position.set(-0.04, 0.52, 0); car.add(win);
    var wg = new THREE.CylinderGeometry(0.11, 0.11, 0.08, 14); wg.rotateX(Math.PI / 2);
    [[-0.28, 0.24], [0.28, 0.24], [-0.28, -0.24], [0.28, -0.24]].forEach(function (p) {
      var w = new THREE.Mesh(wg, dark); w.position.set(p[0], 0.12, p[1]); car.add(w);
    });
    return car;
  }
  function buildHero(THREE, hero, canvas, st) {
    var stage = hero.querySelector('.pg-hero__stage');
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, powerPreference: 'high-performance', logarithmicDepthBuffer: true });   // log depth: near-coplanar faces never flicker
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.0;
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
    camera.position.set(4.6, 3.6, 12.2); camera.lookAt(0, 0.4, 0);   // low three-quarter view: into the cubbies, ramp in front

    scene.add(new THREE.HemisphereLight(0xfff1e0, 0xe8d2b8, 0.9));
    var key = new THREE.DirectionalLight(0xfff4e6, 1.6); key.position.set(-4, 9, 7); scene.add(key);
    var fill = new THREE.DirectionalLight(0xffc9a3, 0.5); fill.position.set(7, 4, -6); scene.add(fill);

    var wood = new THREE.MeshStandardMaterial({ map: woodTexture(THREE, st.wood), roughness: 0.8, metalness: 0 });
    var paint = new THREE.MeshStandardMaterial({ color: new THREE.Color(st.hex), roughness: 0.7, metalness: 0 });
    var dark = new THREE.MeshStandardMaterial({ color: 0x2c1c12, roughness: 0.7 });
    var sign = signCanvas(); drawSign(sign, st.name || st.defaultName, st.dark, st.hex);
    var signTex = new THREE.CanvasTexture(sign); signTex.colorSpace = THREE.SRGBColorSpace; signTex.anisotropy = 4;
    var signMat = new THREE.MeshStandardMaterial({ map: signTex, roughness: 0.7 });

    // Proportions (1 unit ~ 10 cm). Large: 74 x 38.5 x 69 cm, 3 floors + roof. Small: 58 x 42 x 41 cm, 2 floors + roof.
    var world = new THREE.Group(); scene.add(world);
    var garage = new THREE.Group(); world.add(garage);
    var parts = [], cars = [], rampCar = null, ramp = null, rampInfo = null;
    var carColors = [0xff8a3d, 0xc2472b, 0x5f7f5e, 0x2f4160, 0xf2c30d, 0xfff4e6, 0xa63a5b, 0x4b8fcf];
    function clearGarage() { while (garage.children.length) garage.remove(garage.children[0]); parts = []; cars = []; }
    function add(mesh) { garage.add(mesh); parts.push(mesh); return mesh; }
    function build(size) {
      clearGarage();
      var W = size === 'Large' ? 7.4 : 5.8, D = size === 'Large' ? 3.85 : 4.2, H = size === 'Large' ? 6.9 : 4.1;
      var floors = size === 'Large' ? 3 : 2, T = 0.12;
      var fh = (H - 0.9) / floors;                     // floor pitch, roof sign takes the top 0.9
      // sides
      add(new THREE.Mesh(sidePanelGeometry(THREE, D, H, T), paint)).position.x = -W / 2 + T / 2;
      add(new THREE.Mesh(sidePanelGeometry(THREE, D, H, T), paint)).position.x = W / 2 - T / 2;
      // floors + back + dividers
      var cols = size === 'Large' ? 7 : 5, cw = (W - 2 * T) / cols;
      for (var f = 0; f <= floors; f++) {
        var y = f * fh, slab = add(new THREE.Mesh(new THREE.BoxGeometry(W - 2 * T - 0.01, T, D * (f === 0 ? 1 : 0.92)), wood)); slab.position.set(0, y + T / 2, f === 0 ? 0 : -D * 0.04);
        if (f > 0 && f < floors) {   // back wall + dividers for the parking cubbies on the upper floors
          var back = add(new THREE.Mesh(new THREE.BoxGeometry(W - 2 * T - 0.01, fh - T, T), wood)); back.position.set(0, y + T + (fh - T) / 2, -D / 2 + T / 2);
          for (var c = 1; c < cols; c++) {
            var div = add(new THREE.Mesh(new THREE.BoxGeometry(T, fh - T, D * 0.78), wood)); div.position.set(-W / 2 + T + c * cw, y + T + (fh - T) / 2, -D * 0.08);
          }
          // a parked car in most cubbies
          for (var c2 = 0; c2 < cols; c2++) {
            if ((c2 + f) % 3 === 1) continue;
            var car = makeCar(THREE, carColors[(c2 * 3 + f) % carColors.length], dark);
            car.position.set(-W / 2 + T + (c2 + 0.5) * cw, y + T, -D * 0.1 + ((c2 + f) % 2) * 0.25); car.scale.setScalar(Math.min(cw / 1.15, 0.78));
            car.rotation.y = Math.PI / 2 + ((c2 + f) % 2 ? 0.08 : -0.06);
            add(car); cars.push(car);
          }
        }
      }
      // ground floor: arched front wall (the parking bays)
      var arch = add(new THREE.Mesh(archWallGeometry(THREE, W - 2 * T - 0.01, fh - T - 0.01, T, size === 'Large' ? 3 : 2), paint)); arch.position.set(0, T + 0.005, D / 2 - T / 2 + 0.006);   // proud of the slab front, never flush
      // a couple of trucks in the ground-floor bays
      for (var b = 0; b < (size === 'Large' ? 2 : 1); b++) {
        var tr = makeCar(THREE, carColors[(b + 4) % carColors.length], dark); tr.position.set(-W / 4 + b * W / 2.1, T, D * 0.05); tr.rotation.y = Math.PI / 2; tr.scale.setScalar(0.9); add(tr); cars.push(tr);
      }
      // roof: top slab + the name board (arched front board) + a thin cream rail under it
      var top = floors * fh;
      var board = add(new THREE.Mesh(new THREE.BoxGeometry(W - 2 * T - 0.01, 0.9, T), paint)); board.position.set(0, top + 0.45 + T, D / 2 - T / 2 - 0.02);
      var face = add(new THREE.Mesh(new THREE.PlaneGeometry(W - 2 * T - 0.3, 0.7), signMat)); face.position.set(0, top + 0.47 + T, D / 2 + 0.005);
      // roof slab sits ON the side panels (bottom at H + 0.01), never flush with their tops: two faces at the same depth z-fight and the
      // top edge flickered navy/wood ("stuttering", Ninos 2026-11-03)
      var coping = add(new THREE.Mesh(new THREE.BoxGeometry(W + 0.02, T, D + 0.02), wood)); coping.position.set(0, H + T / 2 + 0.01, 0);
      // ramp: from the top floor's front edge (right side) down to the floor in front, plus its car
      var topY = (floors - 1) * fh + T, startX = W * 0.28, endX = -W * 0.22, len = Math.sqrt(Math.pow(startX - endX, 2) + Math.pow(topY - T * 0.4, 2) + Math.pow(D * 0.95 - 0.03, 2));
      ramp = add(new THREE.Mesh(new THREE.BoxGeometry(1.1, T * 0.8, len), wood));
      var s0 = new THREE.Vector3(startX, topY + 0.03, D / 2 + 0.03), s1 = new THREE.Vector3(endX, T * 0.4 + 0.03, D / 2 + D * 0.95);   // the plank starts AT the front edge: started 0.3 inside, the arch wall's top edge pierced it and the paint showed through
      ramp.position.copy(s0.clone().add(s1).multiplyScalar(0.5));
      ramp.lookAt(s1); // z axis points down the ramp
      var kerbL = add(new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, len), wood)); kerbL.position.copy(ramp.position); kerbL.quaternion.copy(ramp.quaternion); kerbL.translateX(-0.5); kerbL.translateY(T * 0.4 + 0.08 + 0.006);   // kerb rests ON the ramp top, not inside it
      var kerbR = kerbL.clone(); kerbR.translateX(1.0); add(kerbR);
      rampInfo = { s0: s0, s1: s1, q: ramp.quaternion.clone() };
      rampCar = makeCar(THREE, 0xff8a3d, dark); rampCar.scale.setScalar(0.82); add(rampCar);
      // shadow pool on the rug
      var pool = add(new THREE.Mesh(new THREE.PlaneGeometry(W * 1.9, D * 2.6), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(shadowSprite()), transparent: true, depthWrite: false })));
      pool.rotation.x = -Math.PI / 2; pool.position.set(0, -0.012, D * 0.25);   // BELOW the floor slab's bottom: at +0.004 it cut through the slab and the front edge serrated
      // centre + fit
      garage.position.set(0, 0, -D * 0.2);
      var fit = size === 'Large' ? 0.74 : 0.98;   // whole garage + ramp inside the square stage at this camera (Large clipped at 0.92)
      return { fit: fit, H: H };
    }
    var cfg = build(st.size);
    var FIT = cfg.fit;
    world.rotation.y = -0.36;
    world.scale.setScalar(0.001);
    hero.classList.add('pg-hero--3d');

    function resize() {
      var w = stage.clientWidth || 600, h = stage.clientHeight || w;
      renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    }
    resize();
    if (window.ResizeObserver) new ResizeObserver(resize).observe(stage); else window.addEventListener('resize', resize);

    // Intro
    var sObj = { s: 0.001 };
    if (hasGsap) { g.to(sObj, { s: FIT, duration: 1.3, ease: 'power4.out', onUpdate: function () { world.scale.setScalar(sObj.s); } }); }
    else { sObj.s = FIT; world.scale.setScalar(FIT); }

    // React to the build controls
    st.onChange.push(function (s) {
      paint.color.set(s.hex);
      if (wood.map.userData.hex !== s.wood) { var t = woodTexture(THREE, s.wood); t.userData.hex = s.wood; wood.map.dispose(); wood.map = t; wood.needsUpdate = true; }
      drawSign(sign, s.name || s.defaultName, s.dark, s.hex); signTex.needsUpdate = true;
      if (s.size !== st.builtSize) {
        st.builtSize = s.size; cfg = build(s.size); FIT = cfg.fit;
        if (hasGsap) { g.fromTo(sObj, { s: sObj.s * 0.92 }, { s: FIT, duration: 0.9, ease: 'back.out(1.4)', onUpdate: function () { world.scale.setScalar(sObj.s); } }); } else { world.scale.setScalar(FIT); }
      }
    });
    st.builtSize = st.size;
    if (doc.fonts && doc.fonts.load) doc.fonts.load('italic 600 150px Fraunces').then(function () { drawSign(sign, st.name || st.defaultName, st.dark, st.hex); signTex.needsUpdate = true; });

    // Pointer: parallax (fine pointers), drag to spin
    var S = { rotY: 0, dragY: 0, px: 0, py: 0, running: true, visible: true, t: 0, rampT: 0 };
    var dragging = false, lastX = 0;
    if (finePointer) window.addEventListener('pointermove', function (e) {
      S.px = (e.clientX / window.innerWidth - 0.5) * 2; S.py = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
    canvas.addEventListener('pointerdown', function (e) { dragging = true; lastX = e.clientX; if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', function (e) { if (!dragging) return; var dx = e.clientX - lastX; lastX = e.clientX; S.dragY += dx * 0.011; });
    function up() { dragging = false; }
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);

    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { S.visible = en[0].isIntersecting; }, { threshold: 0.25 }).observe(stage);
    doc.addEventListener('visibilitychange', function () { S.running = !doc.hidden; });

    var clock = new THREE.Clock();
    function frame() {
      requestAnimationFrame(frame);
      if (!S.running || !S.visible) return;
      var dt = Math.min(clock.getDelta(), 0.05); S.t += dt;
      // the ramp car: rolls down (eased), rests, then is set back on the top floor. 6.5 s loop.
      S.rampT = (S.rampT + dt) % 6.5;
      if (rampCar && rampInfo) {
        var u = S.rampT < 2.4 ? S.rampT / 2.4 : 1, k = u * u * (3 - 2 * u);
        rampCar.position.lerpVectors(rampInfo.s0, rampInfo.s1, k);
        rampCar.quaternion.copy(rampInfo.q); rampCar.rotateY(Math.PI); rampCar.translateY(0.06);   // wheels on the ramp surface along its normal (ramp is 0.096 thick), not sunk into it. No build() locals in here.
        var fade = S.rampT > 5.6 ? 1 - (S.rampT - 5.6) / 0.9 : (S.rampT < 0.5 ? S.rampT / 0.5 : 1);
        rampCar.scale.setScalar(0.82 * (0.6 + 0.4 * Math.max(0, fade)));
        rampCar.children.forEach(function (m) { if (m.material) m.material.transparent = false; });
      }
      // slow yaw + parallax + drag
      var target = -0.36 + Math.sin(S.t * 0.3) * 0.1 + S.dragY + S.px * 0.18;
      S.rotY += (target - S.rotY) * 0.08;
      world.rotation.y = S.rotY;
      world.rotation.x = S.py * 0.03;
      world.position.y = -cfg.H * FIT * 0.5 + 0.1;
      camera.position.x += ((4.6 + S.px * 0.6) - camera.position.x) * 0.05;
      camera.position.y += ((3.6 + S.py * -0.4) - camera.position.y) * 0.05;
      camera.lookAt(0, 0.2, 0);
      renderer.render(scene, camera);
    }
    frame();
  }

  function initHero() {
    doc.querySelectorAll('[data-pg-hero]').forEach(function (hero) {
      if (hero.hasAttribute('data-pg-ready')) return; hero.setAttribute('data-pg-ready', '');
      runHeroCopy(hero);
      if (reduced || hero.hasAttribute('data-pg-static')) return;   // image mode: no WebGL at all
      var st = heroState(hero);
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); initHero3D(hero, st); } }, { rootMargin: '200px' });
        io.observe(hero);
      } else initHero3D(hero, st);
    });
  }

  /* ---------------- Product page: honour ?color=&size=&name= from the hero's Build link ---------------- */
  function runPrefill() {
    var form = doc.querySelector('.product__info-container'); if (!form) return;
    var q = new URLSearchParams(location.search);
    var color = q.get('color'), size = q.get('size'), name = q.get('name');
    function pick(value) {
      if (!value) return;
      var radios = [].slice.call(doc.querySelectorAll('.product__info-container input[type=radio]'));
      var hit = radios.filter(function (r) { return r.value === value && !r.checked; })[0];
      if (hit) { hit.checked = true; hit.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    function go() {
      pick(size); setTimeout(function () { pick(color); }, 80);
      if (name) { var inp = doc.querySelector('[data-pg-personalise]'); if (inp) { inp.value = name.slice(0, 14); } }
    }
    if (window.customElements && customElements.whenDefined) customElements.whenDefined('variant-radios').then(function () { setTimeout(go, 120); }).catch(go); else go();
  }

  function boot() {
    initHero(); runAnatomy(); runReveals(); runRings(); runTilt(); runPrefill();
    if (hasGsap) { setTimeout(function () { ST.refresh(); }, 600); window.addEventListener('load', function () { ST.refresh(); }); if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () { ST.refresh(); }); }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  doc.addEventListener('shopify:section:load', function () { initHero(); runAnatomy(); runReveals(); runRings(); if (hasGsap) ST.refresh(); });
})();
